import {
  Component,
  computed,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import * as fromGenerated from '../../../_generated';
import { AssistantShareInvitePeopleComponent } from './assistant-share-invite-people.component';

/**
 * Who can access a skill (SB-3325 KD-23, "LAW" option set):
 *   - `shared` — "Only people invited": the owner plus any invited users/groups
 *     (the default; owner-only when no one else is invited)
 *   - `public` — everyone in the organization (org-wide Group(all) grant), gated
 *     behind the `ff-share-assistant` flag
 * The parent maps this onto `SetAssistantAccessDto` when saving. The legacy
 * "Only me" (`private`) option is removed; a legacy owner-only skill maps to
 * `shared` (owner-only "Only people invited") on edit.
 */
export type ShareVisibility = 'shared' | 'public';

/**
 * Map an access-graph snapshot back to the dropdown state: org-wide → public,
 * everything else (invited grants or owner-only) → shared. A legacy owner-only
 * skill (no grants) therefore surfaces as owner-only "Only people invited".
 */
export const accessToShareOption = (
  orgWide: boolean,
  _userCount: number,
  _groupCount: number,
): ShareVisibility => {
  if (orgWide) return 'public';
  return 'shared';
};

interface ShareOption {
  type: ShareVisibility;
  labelKey: string;
  leftIcon: fromSeriousSystem.UiBasicIconsInterface;
}

interface ShareDropdownOption extends fromSeriousSystem.DropdownMenuOption {
  type: ShareVisibility;
  icon: fromSeriousSystem.IconsType;
}

let nextUniqueId = 0;

const SHARE_OPTIONS: Record<ShareVisibility, ShareOption> = {
  shared: {
    type: 'shared',
    labelKey: 'ASSISTANTS.SHARE.INVITED',
    leftIcon: { type: 'uiBasic', icon: 'lock' },
  },
  public: {
    type: 'public',
    labelKey: 'ASSISTANTS.SHARE.ORGANIZATION',
    leftIcon: { type: 'uiBasic', icon: 'organization' },
  },
};

/**
 * The owner-facing "Who can access" share panel: a dropdown ("Only people
 * invited" and, when the `ff-share-assistant` flag enables it, "Everyone in my
 * organization") plus, when "Only people invited" is selected, the user invite
 * picker. Presentational — the parent builder owns data-loading and the
 * save-time reconcile against the access graph.
 */
@Component({
  selector: 'squadbox-assistant-share-section',
  imports: [
    TranslateModule,
    fromSeriousSystem.UseArrowIconDirective,
    fromSeriousSystem.UseIconDirective,
    fromSeriousSystem.DropdownMenuComponent,
    fromSeriousSystem.DropdownMenuTriggerDirective,
    AssistantShareInvitePeopleComponent,
  ],
  template: `
    <!-- Who can access -->
    <div class="flex flex-col gap-2">
      <label
        [for]="buttonId"
        class="typo-caption font-semibold leading-4 h-5 self-start px-1 py-0.5"
      >
        {{ 'ASSISTANTS.SHARE.DROPDOWN_LABEL' | translate }}
      </label>
      <button
        #shareButton
        [id]="buttonId"
        class="
          flex flex-row gap-2 items-center
          bg-shades-white rounded-lg px-3 py-2 h-12
          border border-neutral-200 hover:border-primary-300 focus:outline-hidden
        "
        [sdDropdownMenuTrigger]="shareDropdown"
      >
        @let option = selectedOption();
        <svg [sdUseIcon]="option.leftIcon" class="size-6 shrink-0"></svg>
        <span class="typo-p3 grow min-w-0 truncate text-left">{{
          option.labelKey | translate
        }}</span>
        <svg
          [sdUseArrowIcon]="
            shareDropdownMenuOpened()
              ? 'chevron-small-up'
              : 'chevron-small-down'
          "
          class="size-6 shrink-0"
        ></svg>
      </button>
    </div>

    <!-- Menu definition only (rendered via CDK overlay). hidden keeps its empty
         host out of the :host flex flow so it doesn't add a phantom gap-4 between
         the visibility selector and the invite list. -->
    <sd-dropdown-menu
      #shareDropdown
      class="hidden"
      width="trigger"
      [padded]="true"
      [options]="shareDropdownMenuOptions()"
      (opened)="shareDropdownMenuOpened.set(true)"
      (closed)="shareDropdownMenuOpened.set(false)"
    ></sd-dropdown-menu>

    @if (shareOption() === 'shared') {
      <squadbox-assistant-share-invite-people
        [allUsers]="allUsers()"
        [allGroups]="allGroups()"
        [userId]="currentUserId()"
        [(selectedUsersIds)]="selectedUserIds"
        [(selectedGroupsIds)]="selectedGroupIds"
      ></squadbox-assistant-share-invite-people>
    }
  `,
  styles: [
    `
      @reference '../../../../styles.css';

      :host {
        @apply flex flex-col gap-4;
      }
    `,
  ],
})
export class AssistantShareSectionComponent {
  private readonly translate = inject(TranslateService);

  protected readonly buttonId = `assistant-share-button-${nextUniqueId++}`;

  public readonly shareOption = model.required<ShareVisibility>();
  public readonly selectedUserIds = model.required<string[]>();
  public readonly selectedGroupIds = model.required<string[]>();
  public readonly allUsers = input.required<fromGenerated.UserSummaryView[]>();
  public readonly allGroups =
    input.required<fromGenerated.GroupSummaryView[]>();
  public readonly currentUserId = input<string>('');
  /** Gated by the `everyoneInMyOrganization` share feature flag. */
  public readonly isOrgWideEnabled = input<boolean>(false);

  protected readonly selectedOption = computed(
    () => SHARE_OPTIONS[this.shareOption()],
  );

  public readonly shareDropdownMenuOpened = signal(false);
  public readonly shareDropdownMenuOptions = computed(() => {
    const options: ShareDropdownOption[] = [
      {
        type: 'shared',
        label: this.translate.instant(SHARE_OPTIONS.shared.labelKey) as string,
        icon: { type: 'uiBasic', icon: 'lock' },
        active: this.shareOption() === 'shared',
        action: () => this.shareOption.set('shared'),
      },
    ];

    // Show the org-wide entry when new selection of it is enabled, or when it is
    // the current value — so an already-org-wide skill stays representable and
    // selectable even if the `ff-share-assistant` org-wide sub-flag is now off.
    if (this.isOrgWideEnabled() || this.shareOption() === 'public') {
      options.push({
        type: 'public',
        label: this.translate.instant(SHARE_OPTIONS.public.labelKey) as string,
        icon: { type: 'uiBasic', icon: 'organization' },
        active: this.shareOption() === 'public',
        action: () => this.shareOption.set('public'),
      });
    }

    return options;
  });
}
